import { useState, useRef, useCallback } from "react";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export interface ImageData {
  mimeType: string;
  data: string;
}

export function useImageUpload() {
  const [preview, setPreview] = useState<string | null>(null);
  const [imageData, setImageData] = useState<ImageData | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      alert("Image too large. Please select an image under 10MB.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      alert("Invalid file type. Please select a JPEG, PNG, WebP, or GIF image.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setPreview(base64);
      setImageData({ mimeType: file.type, data: base64.split(",")[1] });
    };
    reader.readAsDataURL(file);
  }, []);

  const removeImage = useCallback(() => {
    setPreview(null);
    setImageData(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  return { preview, imageData, fileInputRef, handleImageSelect, removeImage };
}
